/**
 * Helper module for windowing, i.e. behaviours linked to whether items
 * are visible in the viewport.
 */

Promutuel.windowing = {
  /**
   * Enable viewport events on the element. After calling this,
   * `'viewport*'`events will be triggered automatically.
   * You can then use handlers attached with regular `addEventListener()`.
   *
   * The following events are supported:
   * - `viewportenterattop`: the element just became visible at the top of the viewport,
   * - `viewportenteratbottom`: the element just became visible at the bottom of the viewport,
   * - `viewportenter`: the element just became visible (always fired after one of the two previous events),
   * - `viewportleaveattop`: the element just left the viewport, at the top of the viewport,
   * - `viewportleaveatbottom`: the element just left, at the bottom of the viewport,
   * - `viewportleave`: the element just left (always fired after one of the two previous events).
   *
   * Note that, if you call this method *after* event handlers are attached,
   * an event will be immediately triggered (either `leave` or `enter`,
   * depending on whether the element is visible or not.
   *
   * @param HTMLElement element The element whose movements need to be watched.
   * @param String [name] If provided, a new observer will be created and all its
   *   events will be suffixed with `-<name>`. This system is meant to be used along with options
   *   (see below).
   * @param String[options] Optional object to be passed as options to the IntersectionObserver.
   *   This allows to trigger windowing events with offsets (viewport margins and/or area crossing percentages).
   *   In addition to the event suffix, the options will be passed back to the events in the `detail`
   *   property.
   */
  enableViewportEvents: function(element, name = '', options) {
    if (!this._viewportObservers) this._viewportObservers = new Map();
    if (!this._viewportObservers.has(name)) {
      this._viewportObservers.set(name, new IntersectionObserver((entries, observer) => {
        const suffix = name ? `-${name}` : '';
        entries.forEach(entry => {
          if (entry.isIntersecting) {
            if (entry.boundingClientRect.top >= entry.rootBounds.top)
              entry.target.dispatchEvent(new CustomEvent(`viewportenteratbottom${suffix}`, { detail: options }));
            if (entry.boundingClientRect.bottom <= entry.rootBounds.bottom)
              entry.target.dispatchEvent(new CustomEvent(`viewportenterattop${suffix}`, { detail: options }));
            entry.target.dispatchEvent(new CustomEvent(`viewportenter${suffix}`, { detail: options }));
          }
          else {
            if (entry.boundingClientRect.top >= entry.rootBounds.bottom)
              entry.target.dispatchEvent(new CustomEvent(`viewportleaveatbottom${suffix}`, { detail: options }));
            if (entry.boundingClientRect.bottom <= entry.rootBounds.top)
              entry.target.dispatchEvent(new CustomEvent(`viewportleaveattop${suffix}`, { detail: options }));
            entry.target.dispatchEvent(new CustomEvent(`viewportleave${suffix}`, { detail: options }));
          }
        });
      }, options));
    }

    this._viewportObservers.get(name).observe(element);
  },

  /**
   * Checks (synchronously) if the element is currently (partly or entirely)
   * visible in the viewport.
   * If the element has no width or height, it is not considered visible.
   *
   * @param HTMLElement element
   */
  isElementInViewport(element) {
    if (!element) return false;

    const rect = element.getBoundingClientRect();

    return (
         (rect.height > 0) && (rect.top  <= window.innerHeight) && (rect.bottom >= 0)
      && (rect.width  > 0) && (rect.left <= window.innerWidth ) && (rect.right  >= 0)
    );
  },

  /**
   * A set of constants to be used with the `whereIsElement` method.
   */
  POSITIONS: {
    WITHIN_VIEWPORT:       Symbol('WITHIN_VIEWPORT'),
    AFTER_VIEWPORT_END:    Symbol('AFTER_VIEWPORT_END'),
    BEFORE_VIEWPORT_START: Symbol('BEFORE_VIEWPORT_START'),
    INVISIBLE:             Symbol('INVISIBLE'),
  },

  /**
   * Checks (synchronously) where the element currently is in reference to the
   * viewport, considering the horizontal axis only.
   * An element with no width or height is always considered invisible,
   * regardless of where it actually may be.
   *
   * @param HTMLElement element
   * @returns Symbol A constant representing the position (see `POSITIONS`).
   */
  whereIsElementX(element) {
    if (!element) return this.POSITIONS.INVISIBLE;

    const rect = element.getBoundingClientRect();

    if (rect.height <= 0 || rect.width <= 0) return this.POSITIONS.INVISIBLE;
    else if (rect.bottom < 0) return this.POSITIONS.BEFORE_VIEWPORT_START;
    else if (rect.top > window.innerHeight) return this.POSITIONS.AFTER_VIEWPORT_END;
    else return this.POSITIONS.WITHIN_VIEWPORT;
  },

  /**
   * Same as `whereIsElementX`but for the vertical axis.
   *
   * @param HTMLElement element
   * @returns Symbol A constant representing the position (see `POSITIONS`).
   */
  whereIsElementY(element) {
    if (!element) return this.POSITIONS.INVISIBLE;

    const rect = element.getBoundingClientRect();

    if (rect.height <= 0 || rect.width <= 0) return this.POSITIONS.INVISIBLE;
    else if (rect.right < 0) return this.POSITIONS.BEFORE_VIEWPORT_START;
    else if (rect.left > window.innerWidth) return this.POSITIONS.AFTER_VIEWPORT_END;
    else return this.POSITIONS.WITHIN_VIEWPORT;
  },
}
